import * as React from 'react';
import { cx } from 'linaria';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import { Typography } from '../index';
import * as styles from './tooltip.styles';

export interface TooltipPropsBase {
    /**
     *  Position of the tooltip
     *  @default bottom
     */
    position?: 'top' | 'left' | 'right' | 'bottom';

    /**
     * variant
     */
    variant?: 'alert' | 'default';

    /**
     * Whether tooltip should hide the arrow pointing at the trigger.
     */
    noArrow?: boolean;

    /**
     * Toggle disabling of tooltip.
     */
    disabled?: boolean;

    /**
     * Manual set open
     */
    isOpen?: boolean;

    /**
     * Delay in ms before showing tooltip.
     */
    delay?: number;

    /**
     *  Native HTML class attribute for the tooltip element
     */
    className?: string;
    /**
     *  Test id for unit and e2e
     */
    testId?: string;
}

export interface TooltipPropsNormal extends TooltipPropsBase {
    /**
     * Content to be displayed in the tooltip
     */
    text: React.ReactNode;

    /**
     *  Trigger element for the tooltip. Note: Only one child is allowed.
     */
    children: React.ReactNode;

    ellipsify?: never;
}

export interface TooltipPropsEllipsify extends TooltipPropsBase {
    /**
     * Ellipsify overflowing content and only display tooltip when overflowing. Replaces `text` prop and children.
     */
    ellipsify: string;

    text?: never;
    children?: never;
}

export type TooltipProps = TooltipPropsNormal | TooltipPropsEllipsify;

const DELAY_DURATION = 700;

export const Tooltip: React.FunctionComponent<React.PropsWithChildren<TooltipProps>> = (props) => {
    const {
        text,
        children,
        disabled,
        className,
        position = 'bottom',
        noArrow = false,
        testId,
        ellipsify,
        delay = DELAY_DURATION,
        variant = 'default',
        isOpen = false,
    } = props;

    // Hooks
    const opening = React.useRef(false);
    const timer = React.useRef<NodeJS.Timeout>();
    const ref = React.useRef<HTMLDivElement>();

    // Local State
    const [open, setOpen] = React.useState(isOpen);

    // Variables
    let attributes = { open: isOpen || undefined };
    let trigger = children;
    let content = text;

    React.useEffect(
        () => () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        },
        [],
    );

    if (ellipsify) {
        attributes = { open: open || undefined };
        const handleMouseEnter = () => {
            if (ref.current && ref.current.offsetWidth < ref.current.scrollWidth) {
                opening.current = true;
                if (timer.current) {
                    clearTimeout(timer.current);
                }
                timer.current = setTimeout(() => opening.current && setOpen(true), delay);
            }
        };
        const handleMouseLeave = () => {
            opening.current = false;
            setOpen(false);
        };
        trigger = (
            <div
                ref={ref}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                }}
            >
                {ellipsify}
            </div>
        );
        content = ellipsify;
    }

    const textColor = variant === 'alert' ? 'catapult-black' : 'catapult-white';

    return (
        <RadixTooltip.Provider delayDuration={delay}>
            <RadixTooltip.Root defaultOpen={false} {...attributes}>
                <RadixTooltip.Trigger asChild>{trigger}</RadixTooltip.Trigger>
                {!disabled ? (
                    <RadixTooltip.Content side={position} sideOffset={!noArrow ? 2 : 4} asChild data-testid={testId}>
                        <div
                            data-theme="light"
                            className={cx(styles.tooltip, styles.backgroundColors[variant], styles.content, className)}
                        >
                            <Typography variant="caption-1" color={textColor}>
                                {content}
                            </Typography>
                            {noArrow ? null : (
                                <RadixTooltip.Arrow width={12} height={6} className={cx(styles.arrowColor[variant])} />
                            )}
                        </div>
                    </RadixTooltip.Content>
                ) : null}
            </RadixTooltip.Root>
        </RadixTooltip.Provider>
    );
};

export default Tooltip;
